import React from 'react';
import { Box,  useTheme,  } from '@mui/material';
// import { PictureAsPdf, InsertDriveFile, ExpandMore, ExpandLess } from '@mui/icons-material';
import CanvasPdfViewer from './CanvasPdfViewer';
import type { Feed } from '@/constants/types';

// PDF viewer is now handled by CanvasPdfViewer component using vanilla PDF.js

interface PdfViewerProps {
  url: string;
  fileName?: string;
  initialScale?: number;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({ url, fileName, initialScale = 1.0 }) => {
  return (
    <CanvasPdfViewer 
      url={url} 
      fileName={fileName} 
      initialScale={initialScale} 
    />
  );
};

interface FeedMediaProps {
  media: Feed['media'];
  onImageClick: (url: string, alt: string) => void;
  onDocumentClick: (url: string, fileName: string) => void;
}

const isDocumentType = (item: any) => {
  const mimeType = item.mimeType || item.contentType || '';
  return mimeType === 'application/pdf' ||
         mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
         mimeType === 'application/msword' ||
         mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
         mimeType === 'application/vnd.ms-powerpoint';
};

const isPdfType = (item: any) => {
  const mimeType = item.mimeType || item.contentType || '';
  return mimeType === 'application/pdf';
};

const FeedMedia: React.FC<FeedMediaProps> = ({ media, onImageClick }) => {
  const theme = useTheme();
  // const [expandedDocs, setExpandedDocs] = useState<Set<number>>(new Set());

  if (!media || media.length === 0) return null;

  // const toggleDocumentPreview = (index: number) => {
  //   const newExpanded = new Set(expandedDocs);
  //   if (newExpanded.has(index)) newExpanded.delete(index);
  //   else newExpanded.add(index);
  //   setExpandedDocs(newExpanded);
  // };

  return (
    <Box sx={{ mt: 1.5, mx: -2 }}>
      {media.map((item, index) => (
        <Box key={index} sx={{ mb: 0.5 }}>
          {item.type === 'image' ? (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxHeight: 500,
                overflow: 'hidden',
                cursor: 'pointer',
                bgcolor: theme.palette.mode === 'light' ? '#f3f2ef' : '#1d1d1d',
              }}
              onClick={() => onImageClick(item.url, item.originalName)}
            >
              {/* Blurred background for portrait images */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(${item.url})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'blur(25px)',
                  transform: 'scale(1.2)',
                  opacity: 0.6,
                }}
              />
              {/* Main image */}
              <Box
                component="img"
                src={item.url}
                alt={item.originalName}
                sx={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  maxHeight: 500,
                  objectFit: 'contain',
                  zIndex: 1,
                }}
                onError={(e) => { const target = e.target as HTMLImageElement; target.style.display = 'none'; }}
              />
            </Box>
          ) : (
            <Box>
              {/* <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: expandedDocs.has(index) ? '8px 8px 0 0' : 2,
                  bgcolor: 'grey.50',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: 'grey.100', borderColor: 'primary.main', transform: 'translateY(-1px)', boxShadow: 1 },
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}
                  onClick={() => onDocumentClick(item.url, item.originalName || (item as any).fileName)}
                >
                  {(item.mimeType || (item as any).contentType) === 'application/pdf' ? (
                    <PictureAsPdf sx={{ color: 'error.main', fontSize: 24 }} />
                  ) : (
                    <InsertDriveFile sx={{ color: 'text.secondary', fontSize: 24 }} />
                  )}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {item.originalName || (item as any).fileName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Click to preview document
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    {item.size > 1024 * 1024 ? `${(item.size / (1024 * 1024)).toFixed(1)} MB` : `${(item.size / 1024).toFixed(1)} KB`}
                  </Typography>
                </Box>

                {isDocumentType(item) && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); toggleDocumentPreview(index); }}
                    sx={{ ml: 1 }}
                  >
                    {expandedDocs.has(index) ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}
              </Box> */}

              {isDocumentType(item) && (
                <Box sx={{ mx: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: 'background.paper', overflow: 'hidden' }}>
                  {isPdfType(item) ? (
                    <PdfViewer url={item.url} fileName={item.originalName || (item as any).fileName} />
                  ) : (
                    <Box
                      component="iframe"
                      src={`https://docs.google.com/viewer?url=${encodeURIComponent(item.url)}&embedded=true`}
                      sx={{ width: '100%', height: 450, border: 'none' }}
                      title={item.originalName || (item as any).fileName || 'document'}
                      onError={(e) => { console.error('Error loading document preview:', e); }}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default FeedMedia;
